import { createAsyncThunk } from "@reduxjs/toolkit";

import * as basketAPI from "../../../api/basket";
import {
    IDeliveryDatePrediction,
    IPredictionResponseStatusCode,
} from "../../../models/deliverydates";

/**
 * Parameters for loadPredictedDeliveryDate thunk.
 */
interface LoadPredictedDeliveryDateParams {
    postcode: string;
}

/**
 * Return type for loadPredictedDeliveryDate thunk.
 */
type LoadPredictedDeliveryDateResult =
    | {
          payload: IDeliveryDatePrediction;
          error: false;
      }
    | {
          payload: {
              postcode: string;
              status_code: IPredictionResponseStatusCode;
              created_datetime: string | null;
          };
          error: true;
      };

/**
 * Load predicted delivery date for a postcode.
 *
 * Fetches the estimated delivery date for the current basket based on the
 * shipping postcode. The prediction considers product availability, warehouse
 * location, and shipping method. Uses thunk lifecycle states (pending/fulfilled/rejected)
 * to manage loading state automatically.
 *
 * @param {LoadPredictedDeliveryDateParams} params - The delivery date parameters
 * @param {string} params.postcode - The shipping postcode to predict delivery for
 *
 * @returns {Promise<LoadPredictedDeliveryDateResult>} Promise resolving to prediction data or error
 *
 * @example
 * // Load predicted delivery when user enters postcode
 * dispatch(loadPredictedDeliveryDate({ postcode: "90210" }));
 *
 * @example
 * // Handle result with try/catch
 * try {
 *   const result = await dispatch(loadPredictedDeliveryDate({ postcode })).unwrap();
 *   if (!result.error) {
 *     console.log("Delivery by:", result.payload.predicted_delivery_date);
 *   }
 * } catch (error) {
 *   console.error("Failed to load delivery date");
 * }
 *
 * @sideEffects
 * - Updates `data.predicted_delivery_date` with loading/loaded/error state via extraReducers
 * - State automatically transitions through pending → fulfilled/rejected
 * - No manual loading action dispatch needed
 */
export const loadPredictedDeliveryDate = createAsyncThunk(
    "checkout/loadPredictedDeliveryDate",
    async (
        params: LoadPredictedDeliveryDateParams,
    ): Promise<LoadPredictedDeliveryDateResult> => {
        const { postcode } = params;

        try {
            const data = await basketAPI.getPredictedDeliveryDate(postcode);

            if (data.status_code === "success") {
                return {
                    payload: data,
                    error: false,
                };
            } else {
                return {
                    payload: {
                        postcode,
                        status_code: data.status_code,
                        created_datetime: null,
                    },
                    error: true,
                };
            }
        } catch (e) {
            console.error(e);
            return {
                payload: {
                    postcode,
                    status_code: "unknown",
                    created_datetime: null,
                },
                error: true,
            };
        }
    },
);
